/**
 * @file picking-empty.tsx
 * @description Пустые состояния очереди и журнала заданий на сборку
 * (phase19.14; phase19.23, A1; phase19.31 шаг О4 — данными для
 * `ResponsiveList`, кнопки — стандартная `outline` без своей высоты).
 *
 * Четыре повода — четыре текста: «ничего не нашли», «на вас нет»,
 * «недостач нет», «заданий нет». У каждого — что сделать дальше.
 */

import { ClipboardCheck, ClipboardList, Search } from "lucide-react";
import { Link } from "react-router";

import type { QueueTab } from "./usePickingQueue";
import { Button } from "../../ui/button";
import type { ListEmptySpec, ListFilteredEmptySpec } from "../../ui/responsive-list-types";

/** Кнопка-переход в доску заказов — стандартная `outline`. */
function boardButton(ordersBoardPath: string) {
  return (
    <Button asChild variant="outline">
      <Link to={ordersBoardPath}>Открыть доску заказов</Link>
    </Button>
  );
}

/** Пусто в очереди — по активной пилюле. */
export function queueEmptySpec(tab: QueueTab, onShowAll: () => void, ordersBoardPath: string): ListEmptySpec {
  if (tab === "mine") {
    return {
      icon: ClipboardList,
      title: "На вас заданий нет",
      description: "Откройте «Все» и возьмите задание в работу.",
      action: (
        <Button variant="outline" onClick={onShowAll}>
          Показать все
        </Button>
      ),
    };
  }
  if (tab === "shortage") {
    return {
      icon: ClipboardList,
      title: "Заданий с недостачей нет",
      description: "Это хорошая новость: собранное сходится с заказами.",
    };
  }
  return {
    icon: ClipboardList,
    title: "Заданий на сборку нет",
    description: "Задание появляется автоматически, когда заказ переходит на стадию «На сборке».",
    action: boardButton(ordersBoardPath),
  };
}

/** Ничего не нашлось по номеру в очереди. */
export function queueFilteredSpec(onReset: () => void): ListFilteredEmptySpec {
  return {
    icon: Search,
    title: "По этому номеру заданий нет",
    description: "Проверьте номер заказа или сбросьте поиск.",
    onReset,
    resetLabel: "Сбросить поиск",
  };
}

/** Журнал пуст. */
export function historyEmptySpec(ordersBoardPath: string): ListEmptySpec {
  return {
    icon: ClipboardCheck,
    title: "Проведённых заданий пока нет",
    description: "Сюда попадают задания после проведения или отмены.",
    action: boardButton(ordersBoardPath),
  };
}

/** Ничего не нашлось по номеру в журнале (ищем по загруженным страницам). */
export function historyFilteredSpec(onReset: () => void): ListFilteredEmptySpec {
  return {
    icon: Search,
    title: "По этому номеру в журнале ничего нет",
    description: "Ищем по загруженным страницам — нажмите «Показать ещё» или сбросьте поиск.",
    onReset,
    resetLabel: "Сбросить поиск",
  };
}
